import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useToast, ToastContainer } from '../../components/common/Toast'
import apiService from '../../services/apiService'

const EditUser = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const { toasts, removeToast, success, error } = useToast()
  const [states, setStates] = useState([])
  const [loading, setLoading] = useState(false)
  const [fetchingUser, setFetchingUser] = useState(true)
  const [fieldErrors, setFieldErrors] = useState({})
  const [formData, setFormData] = useState({
    association_name: '',
    state_id: '',
    president: '',
    secretary: '',
    official_contact: '',
    official_email: '',
    address: '',
    username: '',
    password: ''
  })

  const token = localStorage.getItem('tenant_admin_token')

  useEffect(() => {
    fetchStates()
    fetchUser()
  }, [id])

  const fetchStates = async () => {
    try {
      const response = await apiService.get('/client-admin/users/states')
      if (response.data.success) {
        setStates(response.data.data)
      }
    } catch (err) {
      console.error('Error fetching states:', err)
    }
  }

  const fetchUser = async () => {
    try {
      setFetchingUser(true)
      const response = await apiService.get(`/client-admin/users/${id}`)
      if (response.data.success) {
        const user = response.data.data
        setFormData({
          association_name: user.association_name || '',
          state_id: user.state_id || '',
          president: user.president || '',
          secretary: user.secretary || '',
          official_contact: user.official_contact || '',
          official_email: user.official_email || '',
          address: user.address || '',
          username: user.username || '',
          password: '' // Don't populate password
        })
      }
    } catch (err) {
      console.error('Error fetching user:', err)
      error('Failed to fetch user data')
    } finally {
      setFetchingUser(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target

    // Clear error for this field when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: null }))
    }

    // For phone number field, only allow digits and limit to 10
    if (name === 'official_contact') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10)
      setFormData(prev => ({ ...prev, [name]: digitsOnly }))
      return
    }

    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setFieldErrors({}) // Clear previous errors

    try {
      // Don't send password if it's empty
      const dataToSend = { ...formData }
      if (!dataToSend.password) {
        delete dataToSend.password
      }

      await apiService.put(`/client-admin/users/${id}`, dataToSend)
      success('User updated successfully')

      // Navigate back to user management after 1.5 seconds
      setTimeout(() => {
        navigate('/admin/users')
      }, 1500)
    } catch (err) {
      console.error('Error updating user:', err)

      // Check if there are validation errors
      if (err.response?.data?.errors) {
        const errors = err.response.data.errors
        const formattedErrors = {}

        // Convert array errors to single message for each field
        Object.keys(errors).forEach(field => {
          formattedErrors[field] = Array.isArray(errors[field])
            ? errors[field][0]
            : errors[field]
        })

        setFieldErrors(formattedErrors)
        error('Please fix the highlighted errors')
      } else {
        error(err.response?.data?.message || 'Failed to update user')
      }
    } finally {
      setLoading(false)
    }
  }

  if (fetchingUser) {
    return (
      <div className="edit-user-page">
        <div className="loading-container">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Loading user data...</p>
        </div>
        <style>{`
          .edit-user-page {
            padding: 0;
            width: 100%;
            min-height: 100vh;
            background: #f3f4f6;
          }
          .loading-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 50vh;
            color: #6b7280;
          }
          .loading-container i {
            font-size: 2rem;
            margin-bottom: 1rem;
          }
        `}</style>
      </div>
    )
  }

  return (
    <div className="edit-user-page">
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      {/* Page Header with gradient background */}
      <div className="page-header-banner">
        <div className="header-content">
          <div className="header-icon">
            <i className="fas fa-user-edit"></i>
          </div>
          <div className="header-text">
            <h1>Edit User</h1>
            <p>Update association user information</p>
          </div>
        </div>
        <button className="btn-back" style={{ marginLeft: 'auto' }} onClick={() => navigate('/admin/users')}>
          <i className="fas fa-arrow-left"></i> Back
        </button>
      </div>

      <div className="form-container">
        <form onSubmit={handleSubmit}>
          {/* Association Information Section */}
          <div className="form-section">
            <h3><i className="fas fa-building"></i> Association Information</h3>
            <div className="form-grid">
              <div className={`form-group full-width ${fieldErrors.association_name ? 'has-error' : ''}`}>
                <label>Association Name <span className="required">*</span></label>
                <input
                  type="text"
                  name="association_name"
                  value={formData.association_name}
                  onChange={handleInputChange}
                  placeholder="Enter Association Name"
                  required
                  className={fieldErrors.association_name ? 'input-error' : ''}
                />
                {fieldErrors.association_name && (
                  <span className="error-message"><i className="fas fa-exclamation-circle"></i> {fieldErrors.association_name}</span>
                )}
              </div>
              <div className={`form-group ${fieldErrors.state_id ? 'has-error' : ''}`}>
                <label>State <span className="required">*</span></label>
                <select
                  name="state_id"
                  value={formData.state_id}
                  onChange={handleInputChange}
                  required
                  className={fieldErrors.state_id ? 'input-error' : ''}
                >
                  <option value="">Select State</option>
                  {states.map(state => (
                    <option key={state.id} value={state.id}>{state.name}</option>
                  ))}
                </select>
                {fieldErrors.state_id && (
                  <span className="error-message"><i className="fas fa-exclamation-circle"></i> {fieldErrors.state_id}</span>
                )}
              </div>
              <div className={`form-group ${fieldErrors.president ? 'has-error' : ''}`}>
                <label>President <span className="required">*</span></label>
                <input
                  type="text"
                  name="president"
                  value={formData.president}
                  onChange={handleInputChange}
                  placeholder="Enter President Name"
                  required
                  className={fieldErrors.president ? 'input-error' : ''}
                />
                {fieldErrors.president && (
                  <span className="error-message"><i className="fas fa-exclamation-circle"></i> {fieldErrors.president}</span>
                )}
              </div>
              <div className={`form-group ${fieldErrors.secretary ? 'has-error' : ''}`}>
                <label>Secretary <span className="required">*</span></label>
                <input
                  type="text"
                  name="secretary"
                  value={formData.secretary}
                  onChange={handleInputChange}
                  placeholder="Enter Secretary Name"
                  required
                  className={fieldErrors.secretary ? 'input-error' : ''}
                />
                {fieldErrors.secretary && (
                  <span className="error-message"><i className="fas fa-exclamation-circle"></i> {fieldErrors.secretary}</span>
                )}
              </div>
            </div>
          </div>

          {/* Contact Information Section */}
          <div className="form-section">
            <h3><i className="fas fa-phone-alt"></i> Contact Information</h3>
            <div className="form-grid">
              <div className={`form-group ${fieldErrors.official_contact ? 'has-error' : ''}`}>
                <label>Official Contact No <span className="required">*</span></label>
                <input
                  type="tel"
                  name="official_contact"
                  value={formData.official_contact}
                  onChange={handleInputChange}
                  placeholder="Enter 10-digit Mobile Number"
                  pattern="[0-9]{10}"
                  maxLength="10"
                  required
                  className={fieldErrors.official_contact ? 'input-error' : ''}
                />
                {fieldErrors.official_contact ? (
                  <span className="error-message"><i className="fas fa-exclamation-circle"></i> {fieldErrors.official_contact}</span>
                ) : (
                  <small className="field-hint">Enter 10-digit mobile number (digits only)</small>
                )}
              </div>
              <div className={`form-group ${fieldErrors.official_email ? 'has-error' : ''}`}>
                <label>Official Email ID <span className="required">*</span></label>
                <input
                  type="email"
                  name="official_email"
                  value={formData.official_email}
                  onChange={handleInputChange}
                  placeholder="Enter Email ID"
                  required
                  className={fieldErrors.official_email ? 'input-error' : ''}
                />
                {fieldErrors.official_email && (
                  <span className="error-message"><i className="fas fa-exclamation-circle"></i> {fieldErrors.official_email}</span>
                )}
              </div>
              <div className={`form-group full-width ${fieldErrors.address ? 'has-error' : ''}`}>
                <label>Address</label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="Enter Full Address"
                  rows="3"
                  className={fieldErrors.address ? 'input-error' : ''}
                />
                {fieldErrors.address && (
                  <span className="error-message"><i className="fas fa-exclamation-circle"></i> {fieldErrors.address}</span>
                )}
              </div>
            </div>
          </div>

          {/* Login Credentials Section */}
          <div className="form-section">
            <h3><i className="fas fa-lock"></i> Login Credentials</h3>
            <div className="form-grid">
              <div className={`form-group ${fieldErrors.username ? 'has-error' : ''}`}>
                <label>User Name <span className="required">*</span></label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  placeholder="Enter Username for Login"
                  required
                  className={fieldErrors.username ? 'input-error' : ''}
                />
                {fieldErrors.username && (
                  <span className="error-message"><i className="fas fa-exclamation-circle"></i> {fieldErrors.username}</span>
                )}
              </div>
              <div className={`form-group ${fieldErrors.password ? 'has-error' : ''}`}>
                <label>Password <span className="optional">(Leave blank to keep current, min 6 characters)</span></label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Enter New Password"
                  className={fieldErrors.password ? 'input-error' : ''}
                />
                {fieldErrors.password && (
                  <span className="error-message"><i className="fas fa-exclamation-circle"></i> {fieldErrors.password}</span>
                )}
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={() => navigate('/admin/users')}>
              <i className="fas fa-times"></i> Cancel
            </button>
            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> Updating...
                </>
              ) : (
                <>
                  <i className="fas fa-save"></i> Update User
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <style>{`
        .edit-user-page {
          padding: 0;
          width: 100%;
          min-height: 100vh;
          background: #f3f4f6;
        }

        .page-header-banner {
          background: var(--primary-gradient, linear-gradient(to right, #022a5e, #053e88, #022a5e));
          padding: 1.5rem 2rem;
          display: flex;
          align-items: center;
          gap: 1.5rem;
          margin-bottom: 0;
          width: 100%;
        }

        .btn-back {
          height: 40px;
          padding: 0 18px;
          border-radius: 8px;
          border: none;
          background: #c00f15;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          justify-content: center;
          color: white;
          font-weight: 600;
          font-size: 0.9rem;
          white-space: nowrap;
          transition: all 0.2s;
          flex-shrink: 0;
        }

        .btn-back:hover {
          background: #a00d12;
        }

        .header-content {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .header-icon {
          width: 50px;
          height: 50px;
          background: rgba(255,255,255,0.15);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .header-icon i {
          font-size: 1.5rem;
          color: white;
        }

        .header-text h1 {
          font-size: 1.5rem;
          font-weight: 600;
          color: white;
          margin: 0;
        }

        .header-text p {
          color: rgba(255,255,255,0.7);
          margin: 0;
          font-size: 0.875rem;
        }

        .form-container {
          background: white;
          margin: 1.5rem;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          overflow: hidden;
        }

        .form-section {
          padding: 1.5rem 2rem;
          border-bottom: 1px solid #e5e7eb;
        }

        .form-section:last-of-type {
          border-bottom: none;
        }

        .form-section h3 {
          font-size: 1rem;
          font-weight: 600;
          color: #374151;
          margin: 0 0 1.25rem 0;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding-bottom: 0.75rem;
          border-bottom: 1px solid #e5e7eb;
        }

        .form-section h3 i {
          color: var(--primary-color, #022a5e);
          font-size: 0.9rem;
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1.25rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
        }

        .form-group.full-width {
          grid-column: span 2;
        }

        .form-group label {
          font-size: 0.875rem;
          font-weight: 500;
          color: #374151;
          margin-bottom: 0.5rem;
        }

        .required {
          color: #ef4444;
        }

        .optional {
          color: #9ca3af;
          font-weight: 400;
          font-size: 0.75rem;
        }

        .form-group input,
        .form-group select,
        .form-group textarea {
          padding: 0.75rem;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 0.9rem;
          transition: all 0.2s;
          font-family: inherit;
        }

        .form-group textarea {
          resize: vertical;
          min-height: 80px;
        }

        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: var(--primary-color, #022a5e);
          box-shadow: 0 0 0 3px rgba(2, 42, 94, 0.1);
        }

        .form-group input::placeholder {
          color: #9ca3af;
        }

        .field-hint {
          font-size: 0.75rem;
          color: #6b7280;
          margin-top: 0.25rem;
        }

        /* Error Styles */
        .form-group.has-error label {
          color: #dc2626;
        }

        .input-error {
          border-color: #dc2626 !important;
          background-color: #fef2f2 !important;
        }

        .input-error:focus {
          border-color: #dc2626 !important;
          box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.15) !important;
        }

        .error-message {
          display: flex;
          align-items: center;
          gap: 0.35rem;
          font-size: 0.8rem;
          color: #dc2626;
          margin-top: 0.35rem;
          animation: fadeIn 0.2s ease;
        }

        .error-message i {
          font-size: 0.75rem;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-5px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .form-actions {
          padding: 1.5rem 2rem;
          background: #f9fafb;
          display: flex;
          justify-content: flex-end;
          gap: 1rem;
          border-top: 1px solid #e5e7eb;
        }

        .btn-cancel {
          padding: 0.75rem 1.5rem;
          background: white;
          color: #6b7280;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.2s;
        }

        .btn-cancel:hover {
          background: #f3f4f6;
          color: #374151;
        }

        .btn-submit {
          padding: 0.75rem 1.5rem;
          background: var(--primary-color, #022a5e);
          color: white;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.2s;
        }

        .btn-submit:hover:not(:disabled) {
          background: var(--primary-hover, #053e88);
        }

        .btn-submit:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        @media (max-width: 768px) {
          .form-grid {
            grid-template-columns: 1fr;
          }

          .form-group.full-width {
            grid-column: span 1;
          }
        }
      `}</style>
    </div>
  )
}

export default EditUser
